import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import { useShareCampaign, useUnshareCampaign } from '../../lib/api/campaigns';
import { Campaign, CampaignShareLink } from '../../lib/types';

/** Manage the partner-facing share links of a campaign — one per brand, plus
 *  one for the whole campaign. Each brand's page shows only its own
 *  placements under its own intro; turning one on or off never changes
 *  another (Doreen, 2026-10-28). The public page shows only the schedule
 *  (what & when) — never tasks or notes. */

const linkUrl = (token: string) => `${window.location.origin}/share/${token}`;

const ShareRow = ({
  campaign,
  brandId,
  name,
  hint,
  link,
}: {
  campaign: Campaign;
  brandId: string | null;
  name: string;
  hint?: string;
  link?: CampaignShareLink;
}) => {
  const toast = useToast();
  const share = useShareCampaign();
  const unshare = useUnshareCampaign();
  const [intro, setIntro] = useState(link?.intro ?? '');
  useEffect(() => setIntro(link?.intro ?? ''), [link?.intro]);
  const on = !!link?.enabled;
  const introChanged = !!link && intro.trim() !== (link.intro ?? '');

  const turnOn = async () => {
    try {
      const updated = await share.mutateAsync({ id: campaign.id, brand: brandId });
      const fresh = updated.shares?.find((l) => l.brand === brandId);
      if (fresh) {
        await navigator.clipboard.writeText(linkUrl(fresh.token));
        toast.push(`Link for ${name} is on — copied`);
      }
    } catch {
      toast.push('Could not turn on the link');
    }
  };

  const turnOff = async () => {
    try {
      await unshare.mutateAsync({ id: campaign.id, brand: brandId });
      toast.push(`Link for ${name} is off — it no longer works`);
    } catch {
      toast.push('Could not turn off the link');
    }
  };

  const saveIntro = async () => {
    try {
      await share.mutateAsync({ id: campaign.id, brand: brandId, intro });
      toast.push('Text saved');
    } catch {
      toast.push('Could not save the text');
    }
  };

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(linkUrl(link.token));
    toast.push('Link copied');
  };

  return (
    <div style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', background: 'var(--bg-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontWeight: 650, fontSize: 13.5 }}>{name}</div>
        <span
          style={{
            fontSize: 11, padding: '1px 8px', borderRadius: 999,
            background: on ? 'var(--seasonal)' : 'var(--border-strong)', color: on ? '#fff' : 'var(--ink-3)',
          }}
        >
          {on ? 'Shared' : 'Off'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {on ? (
            <>
              <Button variant="ghost" onClick={copy}><Icon name="link" size={13} /> Copy link</Button>
              <Button variant="ghost" onClick={turnOff} disabled={unshare.isPending} style={{ color: 'var(--warn)' }}>
                Stop sharing
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={turnOn} disabled={share.isPending}>
              {link ? 'Turn back on & copy' : 'Turn on & copy link'}
            </Button>
          )}
        </div>
      </div>
      {hint && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{hint}</div>}
      {on && link && (
        <>
          <input
            className="input"
            readOnly
            value={linkUrl(link.token)}
            onFocus={(e) => e.target.select()}
            style={{ marginTop: 8, fontSize: 12 }}
          />
          <div style={{ fontSize: 12, color: 'var(--ink-3)', margin: '10px 0 4px' }}>
            Text at the top of the page{brandId ? '' : ' (empty = the campaign description)'}
          </div>
          <textarea
            className="input"
            rows={2}
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            placeholder={campaign.description || 'A short intro for this partner'}
          />
          {introChanged && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
              <Button variant="ghost" onClick={() => setIntro(link.intro ?? '')}>Undo</Button>
              <Button variant="primary" onClick={saveIntro} disabled={share.isPending}>Save text</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ShareCampaignModal = ({
  open,
  onClose,
  campaign,
}: {
  open: boolean;
  onClose: () => void;
  campaign: Campaign;
}) => {
  const links = campaign.shares ?? [];
  const brands = campaign.brand_details ?? [];

  return (
    <Modal open={open} onClose={onClose} title="Share with partner" maxWidth={640}>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 0 }}>
        Each brand gets its own link: a read-only page with only that brand's placements and
        their dates — no tasks, no internal notes, no statuses. Sharing with one brand never
        changes what another brand sees.
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {brands.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Add brands to the campaign to give each of them their own link.
          </div>
        )}
        {brands.map((b) => (
          <ShareRow
            key={b.id}
            campaign={campaign}
            brandId={b.id}
            name={b.name}
            link={links.find((l) => l.brand === b.id)}
          />
        ))}
        <ShareRow
          campaign={campaign}
          brandId={null}
          name="Whole campaign"
          hint="Every placement of every brand — not for a single partner."
          link={links.find((l) => l.brand === null)}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
};

export default ShareCampaignModal;
